import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Info, History, Mic, Square, Send, Ear, MessageCircle, ShieldAlert, Loader2, Plus, Volume2, VolumeX, Database } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { useVoiceCapture } from '../../hooks/useVoiceCapture';
import { deesseTexts } from '../../utils/deesseTranslations';
import DeesseAvatar from './DeesseAvatar';
import DeesseHistory from './DeesseHistory';
import { ReportAIContentButton } from '../ReportAIContent';

const ALERT_KEY = { manipulation: 'eiManipulation', indoctrination: 'eiIndoctrination', harassment: 'eiHarassment', lie: 'eiLie', danger: 'eiDanger', related: 'eiRelated' };
const SEV_KEY = { low: 'eiLow', medium: 'eiMedium', high: 'eiHigh' };

export default function DeesseVoiceModal({ onClose }) {
  const { language } = useLanguage();
  const T = deesseTexts(language);
  const [mode, setMode] = useState('A');
  const [view, setView] = useState('main'); // main | history
  const [showInfo, setShowInfo] = useState(false);
  const [voiceReply, setVoiceReply] = useState(() => { try { return localStorage.getItem('deesse_voice_reply') !== '0'; } catch { return true; } });
  const [dataset, setDataset] = useState({ enabled: false, my_entries: 0, busy: false });
  const [status, setStatus] = useState('idle');
  const [sessionA, setSessionA] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [handsFree, setHandsFree] = useState(false);
  const [hint, setHint] = useState('');
  const [risk, setRisk] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [heard, setHeard] = useState([]);
  const [voiceEmotions, setVoiceEmotions] = useState([]);
  const [analyzing, setAnalyzing] = useState(0);
  const [err, setErr] = useState('');
  const heardRef = useRef([]);
  const audioRef = useRef(null);
  const handsFreeRef = useRef(false);
  const modeRef = useRef('A');
  const closedRef = useRef(false);
  const sessionBRef = useRef(null);
  const talkRef = useRef(null);
  handsFreeRef.current = handsFree;
  modeRef.current = mode;

  const speak = useCallback(async (reply) => {
    if (!voiceReply) return;
    setStatus('speaking');
    try {
      const r = await axios.post('/api/film-ia/text-to-speech', { text: reply }, { timeout: 60000 });
      if (!r.data?.audio || closedRef.current) { setStatus('idle'); return; }
      const a = new Audio(`data:audio/mp3;base64,${r.data.audio}`);
      audioRef.current = a;
      await new Promise((res) => { a.onended = res; a.onerror = res; a.play().catch(res); });
    } catch { /* tts optional */ }
    setStatus('idle');
    if (handsFreeRef.current && modeRef.current === 'A' && !closedRef.current) setTimeout(() => talkRef.current?.(), 300);
  }, [voiceReply]);

  const onBlob = useCallback(async (blob, ext, kind) => {
    if (closedRef.current) return;
    if (kind === 'single') {
      setStatus('thinking');
      const fd = new FormData();
      fd.append('audio', blob, `q.${ext}`);
      if (sessionA) fd.append('session_id', sessionA);
      fd.append('lang', language);
      fd.append('require_wake', handsFreeRef.current ? '1' : '0');
      try {
        const r = await axios.post('/api/deesse/voice-turn', fd, { timeout: 90000 });
        const d = r.data || {};
        if (!d.wake_detected || !d.reply) {
          setHint(T.eiNoWake);
          setStatus('idle');
          if (handsFreeRef.current && !closedRef.current) setTimeout(() => talkRef.current?.(), 300);
          return;
        }
        setHint('');
        setSessionA(d.session_id);
        setMessages(m => [...m, { role: 'user', content: d.transcript }, { role: 'assistant', content: d.reply }]);
        await speak(d.reply);
      } catch (e) {
        setStatus('idle');
        setErr(e.response?.data?.detail || e.message || T.eiKo);
      }
      return;
    }
    // Mode B chunk
    setAnalyzing(n => n + 1);
    const fd = new FormData();
    fd.append('audio', blob, `chunk.${ext}`);
    if (sessionBRef.current) fd.append('session_id', sessionBRef.current);
    fd.append('lang', language);
    const ctx = heardRef.current.slice(0, 2).map(h => h.text).reverse().join(' ');
    if (ctx) fd.append('context', ctx);
    try {
      setErr('');
      const r = await axios.post('/api/deesse/emotion/analyze', fd, { timeout: 60000 });
      const d = r.data || {};
      if (d.session_id) sessionBRef.current = d.session_id;
      setRisk(d.risk_score || 0);
      if (d.voice_emotions?.length) setVoiceEmotions(d.voice_emotions);
      if (d.transcript) setHeard(h => { const n = [{ text: d.transcript, risk: d.risk_score || 0, ts: d.ts }, ...h].slice(0, 12); heardRef.current = n; return n; });
      if (d.alerts?.length) {
        setAlerts(a => [...d.alerts.map(x => ({ ...x, ts: d.ts })), ...a].slice(0, 30));
        setStatus('alert');
        try { navigator.vibrate?.([120, 60, 120]); } catch { /* noop */ }
        setTimeout(() => setStatus(s => (s === 'alert' ? 'listening' : s)), 2500);
      }
    } catch (e) {
      if (e.response?.status !== 400) setErr(e.response?.data?.detail || e.message || T.eiKo);
    } finally { setAnalyzing(n => Math.max(0, n - 1)); }
  }, [sessionA, language, speak, T.eiKo, T.eiNoWake]);

  const cap = useVoiceCapture(onBlob);

  const talk = useCallback(async () => {
    if (closedRef.current) return;
    setHint('');
    setStatus('listening');
    setErr('');
    try { await cap.single(15000); } catch { setStatus('idle'); setErr(T.eiMicKo); }
  }, [cap, T.eiMicKo]);
  talkRef.current = talk;

  const stopAll = useCallback(() => {
    cap.stop();
    audioRef.current?.pause();
    setHandsFree(false);
    setStatus('idle');
  }, [cap]);

  const sendText = async () => {
    const msg = text.trim();
    if (!msg) return;
    setText('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setStatus('thinking');
    try {
      const r = await axios.post('/api/deesse/chat', { message: msg, session_id: sessionA, lang: language }, { timeout: 90000 });
      setSessionA(r.data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: r.data.reply }]);
      await speak(r.data.reply);
    } catch (e) { setStatus('idle'); setErr(e.response?.data?.detail || e.message || T.eiKo); }
  };

  const startListenB = async () => {
    setStatus('listening');
    setErr('');
    try { await cap.loop(8000); } catch { setErr(T.eiMicKo); }
    setStatus('idle');
  };

  const switchMode = (m) => { stopAll(); setMode(m); };
  const newConv = () => { stopAll(); setSessionA(null); setMessages([]); sessionBRef.current = null; setAlerts([]); setHeard([]); setRisk(0); setVoiceEmotions([]); };
  const resume = async (id) => {
    try {
      const r = await axios.get(`/api/deesse/history/${id}`);
      setSessionA(id); setMessages((r.data.messages || []).filter(m => m.role !== 'heard'));
      setMode('A'); setView('main');
    } catch { toast.error(T.eiKo); }
  };

  useEffect(() => { closedRef.current = false; return () => { closedRef.current = true; audioRef.current?.pause(); }; }, []);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get('/api/deesse/dataset/status'); setDataset(d => ({ ...d, enabled: !!r.data?.enabled, my_entries: r.data?.my_entries || 0 })); } catch { /* ignore */ }
    })();
  }, []);

  const toggleVoice = () => {
    const next = !voiceReply;
    setVoiceReply(next);
    try { localStorage.setItem('deesse_voice_reply', next ? '1' : '0'); } catch { /* ignore */ }
    if (!next) { try { audioRef.current?.pause(); } catch { /* ignore */ } }
  };

  const toggleDataset = async () => {
    setDataset(d => ({ ...d, busy: true }));
    try {
      const r = await axios.post('/api/deesse/dataset/opt-in', { enabled: !dataset.enabled });
      setDataset(d => ({ ...d, enabled: !!r.data?.enabled, busy: false }));
      toast.success(r.data?.enabled ? T.eiDatasetOn : T.eiDatasetOff);
    } catch { setDataset(d => ({ ...d, busy: false })); toast.error(T.eiKo); }
  };

  const eraseMyDataset = async () => {
    if (!window.confirm(T.eiDatasetErase + ' ?')) return;
    try {
      const r = await axios.delete('/api/deesse/dataset/my-entries');
      setDataset(d => ({ ...d, my_entries: 0 }));
      toast.success(`${T.eiDatasetErased} (${r.data?.deleted || 0})`);
    } catch { toast.error(T.eiKo); }
  };

  const close = () => { closedRef.current = true; stopAll(); onClose(); };

  const statusLabel = status === 'listening' ? T.eiListening : status === 'thinking' ? T.eiThinking : status === 'speaking' ? T.eiSpeaking : mode === 'A' ? T.eiIdle : (cap.recording ? T.eiListenActive : T.eiModeBSub);
  const riskColor = risk >= 60 ? 'from-red-500 to-orange-500' : risk >= 30 ? 'from-amber-400 to-yellow-500' : 'from-emerald-400 to-teal-500';

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md overflow-y-auto" data-testid="deesse-modal">
      <div className="min-h-full flex items-start justify-center p-3 pt-6 pb-24">
        <div className="relative w-full max-w-lg rounded-3xl border border-fuchsia-500/30 bg-gradient-to-b from-[#1a0b24] via-[#12071b] to-black p-5 text-white shadow-2xl shadow-fuchsia-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowInfo(v => !v)} className={`p-2 rounded-full ${showInfo ? 'bg-fuchsia-500/40' : 'bg-white/5 hover:bg-white/10'}`} title={T.eiInfoTitle} data-testid="deesse-info-btn"><Info className="w-4 h-4 text-fuchsia-200" /></button>
              <button onClick={() => setView(v => (v === 'history' ? 'main' : 'history'))} className={`p-2 rounded-full ${view === 'history' ? 'bg-fuchsia-500/40' : 'bg-white/5 hover:bg-white/10'}`} title={T.eiHistory} data-testid="deesse-history-btn"><History className="w-4 h-4 text-fuchsia-200" /></button>
              <button onClick={newConv} className="p-2 rounded-full bg-white/5 hover:bg-white/10" title={T.eiNewConv} data-testid="deesse-new-conv"><Plus className="w-4 h-4 text-fuchsia-200" /></button>
              <button onClick={toggleVoice} className={`p-2 rounded-full ${voiceReply ? 'bg-fuchsia-500/40' : 'bg-white/5 hover:bg-white/10'}`} title={T.eiVoiceReply} data-testid="deesse-voice-toggle">
                {voiceReply ? <Volume2 className="w-4 h-4 text-fuchsia-200" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
            <button onClick={close} className="p-2 rounded-full bg-white/5 hover:bg-white/10" data-testid="deesse-close"><X className="w-5 h-5" /></button>
          </div>

          {showInfo && (
            <div className="mb-4 p-3 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/30 text-xs text-fuchsia-100 leading-relaxed space-y-2" data-testid="deesse-info-tooltip">
              <p className="font-bold text-fuchsia-200">{T.eiInfoTitle}</p>
              <p>{T.eiInfoText}</p>
              <div className="mt-2 pt-2 border-t border-fuchsia-500/20" data-testid="deesse-dataset-block">
                <p className="font-bold text-fuchsia-200 flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> {T.eiDatasetTitle}</p>
                <p className="mt-0.5 text-[11px] text-fuchsia-100/85">{T.eiDatasetDesc}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button onClick={toggleDataset} disabled={dataset.busy} className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${dataset.enabled ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-100' : 'bg-white/5 border-white/15 hover:bg-white/10 text-fuchsia-100'}`} data-testid="deesse-dataset-toggle">
                    {dataset.busy ? '…' : dataset.enabled ? `✓ ${T.eiDatasetOn.split('—')[0].trim()}` : T.eiDatasetOff.split('.')[0]}
                  </button>
                  {dataset.enabled && (
                    <span className="text-[10px] text-fuchsia-100/70">{T.eiDatasetEntries} : <strong>{dataset.my_entries}</strong></span>
                  )}
                  {dataset.my_entries > 0 && (
                    <button onClick={eraseMyDataset} className="text-[10px] underline text-red-300 hover:text-red-200" data-testid="deesse-dataset-erase">{T.eiDatasetErase}</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {view === 'history' ? (
            <DeesseHistory T={T} onBack={() => setView('main')} onResume={resume} />
          ) : (
            <>
              <div className="flex flex-col items-center mb-3">
                <DeesseAvatar state={status} level={cap.level} risk={mode === 'B' ? risk : 0} />
                <h3 className="mt-3 text-2xl font-bold bg-gradient-to-r from-fuchsia-300 via-pink-200 to-purple-300 bg-clip-text text-transparent">{T.eiTitle}</h3>
                <p className="text-[11px] text-fuchsia-200/70">{T.eiSubtitle}</p>
                <p className="mt-1 text-xs text-gray-300 min-h-[1rem]" data-testid="deesse-status">{statusLabel}</p>
                {status === 'listening' && mode === 'A' && <p className="text-[11px] text-emerald-300 mt-0.5" data-testid="deesse-tap-hint">{T.eiTapToSend}</p>}
                {status === 'listening' && (
                  <div className="w-40 h-1.5 mt-2 rounded-full bg-white/10 overflow-hidden" data-testid="deesse-level"><div className="h-full bg-emerald-400 transition-[width] duration-100" style={{ width: `${Math.min(100, Math.round(cap.level * 100))}%` }} /></div>
                )}
                {hint && <p className="text-[11px] text-amber-300 mt-0.5" data-testid="deesse-hint">{hint}</p>}
                {err && <p className="text-[11px] text-red-300 mt-0.5" data-testid="deesse-error">{err}</p>}
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4" data-testid="deesse-mode-tabs">
                {[['A', MessageCircle, T.eiModeA, T.eiModeASub], ['B', Ear, T.eiModeB, T.eiModeBSub]].map(([m, Icon, label, sub]) => (
                  <button key={m} onClick={() => switchMode(m)} className={`p-3 rounded-2xl border text-left transition-colors ${mode === m ? 'bg-fuchsia-500/25 border-fuchsia-400/60' : 'bg-white/5 border-white/10 hover:bg-white/10'}`} data-testid={`deesse-mode-${m}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="w-4 h-4 text-fuchsia-200" /> {label}</div>
                    <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
                  </button>
                ))}
              </div>

              {mode === 'A' ? (
                <div data-testid="deesse-mode-a-panel">
                  <div className="space-y-2 max-h-52 overflow-y-auto mb-3 pr-1" data-testid="deesse-messages">
                    {messages.map((m, i) => (
                      <div key={i} className={`text-xs p-2.5 rounded-2xl ${m.role === 'user' ? 'bg-fuchsia-500/25 ml-10' : 'bg-white/5 border border-white/10 mr-10'}`} data-testid={`deesse-msg-${m.role}`}>
                        {m.content}
                        {m.role !== 'user' && (
                          <div className="mt-1.5 flex justify-end"><ReportAIContentButton feature="deesse_voice" contentText={m.content} testId={`ai-report-deesse-${i}`} /></div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <button
                      onClick={status === 'listening' ? () => cap.stop() : talk}
                      disabled={status === 'thinking' || status === 'speaking'}
                      className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-transform active:scale-95 disabled:opacity-50 ${status === 'listening' ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-br from-fuchsia-500 to-pink-600'}`}
                      data-testid="deesse-mic-btn"
                    >
                      {status === 'thinking' ? <Loader2 className="w-6 h-6 animate-spin" /> : status === 'listening' ? <Square className="w-6 h-6" fill="white" /> : <Mic className="w-7 h-7" />}
                    </button>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-gray-300 mb-3 cursor-pointer" data-testid="deesse-handsfree-label">
                    <input type="checkbox" checked={handsFree} onChange={(e) => { setHandsFree(e.target.checked); if (e.target.checked && status === 'idle') talk(); if (!e.target.checked) stopAll(); }} className="accent-fuchsia-500" data-testid="deesse-handsfree-toggle" />
                    {T.eiHandsFree}
                  </label>
                  <div className="flex gap-2">
                    <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendText()} placeholder={T.eiTypePh} className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-fuchsia-400" data-testid="deesse-text-input" />
                    <button onClick={sendText} disabled={!text.trim() || status === 'thinking'} className="px-4 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-40" data-testid="deesse-send-btn"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              ) : (
                <div data-testid="deesse-mode-b-panel">
                  <div className="mb-3">
                    <div className="flex justify-between text-[11px] mb-1"><span className="text-gray-300">{T.eiRisk}</span><span className="font-bold" data-testid="deesse-risk-value">{risk}%</span></div>
                    <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full bg-gradient-to-r ${riskColor} transition-all duration-700`} style={{ width: `${risk}%` }} /></div>
                  </div>
                  <button onClick={cap.recording ? stopAll : startListenB} className={`w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 ${cap.recording ? 'bg-red-500/80 animate-pulse' : 'bg-gradient-to-r from-emerald-500 to-teal-600'}`} data-testid="deesse-listen-toggle">
                    {cap.recording ? <Square className="w-4 h-4" fill="white" /> : <Ear className="w-4 h-4" />} {cap.recording ? T.eiStopListen : T.eiStartListen}
                  </button>
                  {analyzing > 0 && <p className="text-[11px] text-fuchsia-200 mt-2 flex items-center gap-1" data-testid="deesse-analyzing"><Loader2 className="w-3 h-3 animate-spin" /> {T.eiAnalyzing}</p>}
                  {voiceEmotions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid="deesse-voice-emotions">
                      {voiceEmotions.map((e, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/30 text-[10px] text-fuchsia-100">{e.name} {e.score}%</span>)}
                    </div>
                  )}
                  <div className="mt-3 space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="deesse-alerts">
                    {alerts.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-3" data-testid="deesse-no-alert">{T.eiNoAlert}</p>
                    ) : alerts.map((a, i) => (
                      <div key={i} className={`p-2.5 rounded-xl border text-xs ${a.severity === 'high' ? 'bg-red-500/15 border-red-500/40' : a.severity === 'medium' ? 'bg-amber-500/15 border-amber-500/40' : 'bg-white/5 border-white/10'}`} data-testid={`deesse-alert-${a.type}`}>
                        <div className="flex items-center gap-1.5 font-bold text-red-200"><ShieldAlert className="w-3.5 h-3.5" /> {T[ALERT_KEY[a.type]] || a.type} <span className="text-[10px] font-normal text-gray-400">· {T[SEV_KEY[a.severity]] || a.severity}</span></div>
                        {a.excerpt && <p className="italic text-gray-300 mt-1">« {a.excerpt} »</p>}
                        <p className="text-gray-200 mt-1">{a.explanation}</p>
                        {a.advice && <p className="text-emerald-200 mt-1"><span className="font-semibold">{T.eiAdvice} :</span> {a.advice}</p>}
                      </div>
                    ))}
                  </div>
                  {heard.length > 0 && (
                    <div className="mt-3 space-y-1 max-h-28 overflow-y-auto pr-1" data-testid="deesse-heard">
                      <p className="text-[10px] uppercase tracking-wide text-gray-500">{T.eiHeard}</p>
                      {heard.map((h, i) => <p key={i} className="text-[11px] text-gray-400 truncate">· {h.text}</p>)}
                    </div>
                  )}
                </div>
              )}
              <p className="mt-4 text-[10px] text-gray-500 text-center">{T.eiDisclaimer}</p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
